import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import UserRepresentation from '@keycloak/keycloak-admin-client/lib/defs/userRepresentation';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { MessageService } from 'primeng/api';
import { KeycloakProfile } from 'keycloak-js';
import { resolveOrganizationIds } from './auth-organizations';
import { KeycloakAdminService } from './keycloak-admin.service';
import { canWrite, isDemoOrganization } from './write-access';

export interface AuthState {
  roles: string[];
  /**
   * V4+: fine-grained permissions injected by the Keycloak Script Mapper into the JWT
   * `permissions[]` claim. Empty array if the JWT is legacy (pre-V4). Use `hasPermission()`
   * — never read the array directly, since PLATFORM_ADMIN bypasses the contains-check.
   */
  permissions: string[];
  /**
   * Vrai quand le jeton porte le claim `permissions` (même vide). Faux pour un jeton émis par un
   * mapper Keycloak antérieur : `hasPermission` retombe alors sur la table rôle → permissions.
   */
  hasPermissionsClaim: boolean;
  /**
   * V2+: organization UUID from the JWT `org_id` claim. Empty string for PLATFORM_ADMIN /
   * PLATFORM_SUPPORT (no org). Falls back to legacy `customerId` attribute when missing.
   */
  organizationId: string;
  /**
   * ADR-024 : org qui possède l'utilisateur (`tenant_org_id`) — équipe, clés API, audit
   * (usermanager). Égale à `organizationId` sauf pour une org de démo, où `organizationId` est
   * l'org source des données partagées.
   */
  tenantOrganizationId: string;
  profile: KeycloakProfile | null;
  loading: boolean;
  customerId: string;
}

const initialAuthState: AuthState = {
  roles: [],
  permissions: [],
  hasPermissionsClaim: false,
  organizationId: '',
  tenantOrganizationId: '',
  profile: null,
  loading: false,
  customerId: '',
};

/**
 * V7: roles that grant a global bypass on permission checks. PLATFORM_ADMIN passes any check;
 * PLATFORM_SUPPORT passes any `*:read` check. The legacy WHEERE_MAINTAINER / SUPERADMIN aliases
 * have been removed from KC and from the canonical taxonomy.
 */
const PLATFORM_ADMIN_ROLES = ['PLATFORM_ADMIN'];
const PLATFORM_SUPPORT_ROLES = ['PLATFORM_SUPPORT'];

/**
 * V7: Keycloak assigns a handful of built-in roles to every user (account-management,
 * authorization, default-roles-{realm}, etc.). They have nothing to do with Wheere business
 * logic and pollute the `roles` array — `hasAnyRole(['view-profile'])` would accidentally match.
 * We strip them at extraction time so the store only sees real Wheere roles.
 *
 * Pattern-based instead of an explicit list because KC's `default-roles-{realm}` varies per
 * realm name, and future built-ins are easier to swallow with a heuristic.
 */
const KEYCLOAK_BUILT_IN_ROLES = new Set([
  'manage-account',
  'manage-account-links',
  'view-profile',
  'view-applications',
  'view-consent',
  'manage-consent',
  'delete-account',
  'offline_access',
  'uma_authorization',
  'create-realm',
  'admin',
]);

function isWheereRole(role: string): boolean {
  if (!role) return false;
  if (KEYCLOAK_BUILT_IN_ROLES.has(role)) return false;
  // KC seeds a `default-roles-<realm>` composite role on every user — drop any name that starts
  // with `default-roles-` regardless of the realm.
  if (role.startsWith('default-roles-')) return false;
  return true;
}

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withDevtools('auth'),
  withState(initialAuthState),
  withMethods(store => {
    const keycloakAdminClient = inject(KeycloakAdminService).client;
    const toast = inject(MessageService);

    return {
      loadUser: async (payload: { profile: KeycloakProfile; roles: string[]; token?: string }) => {
        const { profile, roles: rawRoles, token } = payload;
        // V7: filter out KC built-in roles (manage-account, offline_access, uma_authorization,
        // default-roles-{realm}, …) so the store only carries real Wheere roles. See
        // KEYCLOAK_BUILT_IN_ROLES for the full list and the rationale.
        const roles = (rawRoles ?? []).filter(isWheereRole);

        // V5a: extract permissions[] and org_id from the JWT claims (Script Mapper injects these
        // at login). Falls back gracefully on legacy JWTs. The previous Keycloak-Admin
        // `memberOrganizations` round-trip has been dropped — the JWT carries everything we need
        // and the dedicated org list now lives in `domain-users` (`UsersStore.loadOrganizations`).
        const claims = decodeJwtClaims(token);
        const hasPermissionsClaim = Array.isArray(claims['permissions']);
        const permissions = hasPermissionsClaim ? (claims['permissions'] as string[]) : [];
        const { organizationId, tenantOrganizationId } = resolveOrganizationIds(
          claims,
          profile.attributes?.['customer_id'] as string | undefined
        );

        patchState(store, {
          profile,
          roles,
          permissions,
          hasPermissionsClaim,
          organizationId,
          tenantOrganizationId,
          customerId: (profile.attributes?.['customer_id'] as string) ?? '',
        });
      },
      updateProfile: async (id: string, profile: UserRepresentation) => {
        patchState(store, { loading: true });
        try {
          await keycloakAdminClient.users.update({ id }, profile);

          patchState(store, { profile });
          toast.add({ severity: 'success', detail: 'Profile updated' });
        } catch (e) {
          toast.add({ severity: 'error', detail: 'Update profile error' });
          throw e;
        }
        patchState(store, { loading: false });
      },
      resetPassword: async (id: string, password: string) => {
        patchState(store, { loading: true });
        try {
          await keycloakAdminClient.users.resetPassword({ id, credential: { value: password } });
          toast.add({ severity: 'success', detail: 'Password updated' });
        } catch (e) {
          toast.add({ severity: 'error', detail: 'Password reset error' });
          throw e;
        }
        patchState(store, { loading: false });
      },
      hasAnyRole: (requiredRoles: string | string[]) => {
        // V7: PLATFORM_ADMIN bypasses every route guard — they can navigate anywhere across
        // the 3 apps. Otherwise we do the standard "user has at least one of the required
        // roles" check. The legacy SUPERADMIN / WHEERE_MAINTAINER aliases were removed in V7.
        if (PLATFORM_ADMIN_ROLES.some(r => store.roles().includes(r))) return true;

        const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
        return !!rolesToCheck?.some(role => store.roles().includes(role));
      },
      /**
       * Permission fine (ADR-025) : PLATFORM_ADMIN passe, PLATFORM_SUPPORT lit seulement, une org
       * de démo n'écrit jamais les données partagées (ADR-024), sinon le claim `permissions[]`
       * fait foi (repli par rôle si le jeton n'a pas de claim). Règle dans {@link canWrite}.
       *
       * @param required Une permission (`gateway:write`) ou une liste « au moins une de ».
       */
      hasPermission: (required: string | string[]): boolean =>
        canWrite(
          {
            roles: store.roles(),
            permissions: store.hasPermissionsClaim() ? store.permissions() : null,
            isDemoOrganization: isDemoOrganization(store.organizationId(), store.tenantOrganizationId()),
          },
          required
        ),
    };
  }),
  withComputed(store => {
    /**
     * V7-fix: PLATFORM_ADMIN bypass on every legacy `isXxxAdmin` flag. The original signals
     * checked literal V3 role names (TRACKER_ADMIN, GATEWAY_ADMIN, …) that were deleted in the
     * V4 taxonomy refactor. PLATFORM_ADMIN doesn't carry any of those — so every button gated
     * by `[disabled]="!isTrackerAdmin()"` was disabled for our actual super-admin. Same shape as
     * the bypass already in `hasAnyRole` / `hasPermission`.
     *
     * Long term these flags will go away in favour of `hasPermission('tracker:admin')` etc once
     * the V4 Script Mapper ships the `permissions[]` claim. Until then, this is the minimum
     * bypass that unblocks PLATFORM_ADMIN without a wider rewrite.
     */
    const isPlatformAdminFn = () => PLATFORM_ADMIN_ROLES.some(r => store.roles().includes(r));
    /**
     * V8: any platform-level user (PLATFORM_ADMIN or PLATFORM_SUPPORT). Used to gate read-only
     * cross-org features in the UI — owner filter on listings, /users globale, etc. Mirrors the
     * backend `UserSessionService.isPlatformUser()` semantics so the front and the aspect agree.
     */
    const isPlatformUserFn = () => isPlatformAdminFn() || PLATFORM_SUPPORT_ROLES.some(r => store.roles().includes(r));
    return {
      isLoggedIn: computed(() => !!store.profile()),
      fullname: computed(() => `${store.profile()?.firstName} ${store.profile()?.lastName || ''}`),
      email: computed(() => store.profile()?.email),
      id: computed(() => store.profile()?.id),
      idUser: computed(() => store.profile()?.id),
      // V7-fix: legacy V3 role bundles (TRACKER_ADMIN etc.) were removed from KC. Each isXxxAdmin
      // signal now resolves through the PLATFORM_ADMIN bypass alone. Long term these flags will
      // be replaced by `hasPermission('tracker:admin')` etc when the V4 Script Mapper ships the
      // `permissions[]` claim.
      isTrackerAdmin: computed(isPlatformAdminFn),
      isGatewayAdmin: computed(isPlatformAdminFn),
      isSalesAdmin: computed(isPlatformAdminFn),
      isSiteAdmin: computed(isPlatformAdminFn),
      isUserAdmin: computed(isPlatformAdminFn),
      /** V7 canonical "is platform admin" — only `PLATFORM_ADMIN`; legacy aliases dropped. */
      isPlatformAdmin: computed(isPlatformAdminFn),
      /** V8: any platform-level user (admin or support) — read-only cross-org access. */
      isPlatformUser: computed(isPlatformUserFn),
      /** ADR-024 : org de démo — l'utilisateur lit une org source, en lecture seule. */
      isDemoOrganization: computed(() => isDemoOrganization(store.organizationId(), store.tenantOrganizationId())),
    };
  })
);

/**
 * V4: best-effort base64url JWT decode. Returns the payload claims or `{}` on any failure
 * (legacy/missing token, malformed payload). Pure function — no exceptions leak.
 */
function decodeJwtClaims(token: string | undefined): Record<string, unknown> {
  if (!token || typeof token !== 'string') return {};
  const parts = token.split('.');
  if (parts.length !== 3) return {};
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}
